import { inject, Injectable } from '@angular/core'
import { HttpClient, HttpErrorResponse } from '@angular/common/http'
import { EnvironmentsHelperService } from '@environments/core/services/environments-helper.service'
import { WebsocketService } from '@modules/websocket-module/services/websocket.service'
import { catchError, map, Observable, of, tap } from 'rxjs'
import { PositionsOptionsHelperService } from '@modules/options-module/positions/services/positions-options-helper.service'
import { UtilsService } from '@shared/services/utils.service'
import { PositionOptionInterface } from '@modules/options-module/positions/interfaces/position-option.interface'
import { ExecutePositionOptionResponseInterface } from '@modules/options-module/positions/interfaces/execute-position-option-response.interface'
import { ClosePositionInterface } from '@modules/positions-module/interfaces/close-position.interface'
import { OrderInterface } from '@modules/orders-module/interfaces/order.interface'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'
import { UserDetailInterface } from '@modules/user-module/interfaces/user-detail.interface'
import { PositionsMessageInterface } from '../../../positions-module/interfaces/positions-message.interface'

@Injectable({
	providedIn: 'root',
})
export class PositionsOptionsService {
	protected readonly date: DateConstructor = Date

	private readonly _http: HttpClient = inject<HttpClient>(HttpClient)
	private readonly _utilsService: UtilsService = inject<UtilsService>(UtilsService)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)
	private readonly _environmentsHelperService: EnvironmentsHelperService =
		inject<EnvironmentsHelperService>(EnvironmentsHelperService)

	private readonly _positionsOptionsHelperService: PositionsOptionsHelperService =
		inject(PositionsOptionsHelperService)
	private readonly _websocketService: WebsocketService<PositionsMessageInterface, PositionOptionInterface[]> =
		inject<WebsocketService<PositionsMessageInterface, PositionOptionInterface[]>>(WebsocketService)


	public launchSocketPositionsOptions(): Observable<PositionOptionInterface[]> {
		const userDetail: UserDetailInterface | null = this._userHelperService.userDetail$.getValue()
		if (!userDetail) return of([])

		const url: string = `${this._environmentsHelperService.socketUrl}/ws/get_options_positions/`

		this._positionsOptionsHelperService.isLoadingPositionsOptions$.next(true)
		this._websocketService.connect(url)

		if (!this._websocketService.hasMessageInSocket(url)) {
			this._websocketService.sendMessage(url, {
				action: 'get_positions',
				request_id: this._utilsService.generateUniqId(),
				account_id: userDetail.broker_id,
			})
		}

		return this._websocketService.getResponses(url).pipe(
			map((positions: PositionOptionInterface[]): PositionOptionInterface[] => {
				const parsedPosition: PositionOptionInterface[] =
					this._positionsOptionsHelperService.parsePositions(positions)
				return this._utilsService.sortArrayOfObjects<PositionOptionInterface>(
					parsedPosition,
					'unrealized_plpc',
					'desc',
				)
			}),
			tap((positions: PositionOptionInterface[]): void => {
				this._positionsOptionsHelperService.isLoadingPositionsOptions$.next(false)
				this._positionsOptionsHelperService.positionsOptions$.next(positions)
			}),
			catchError((error: HttpErrorResponse): never => {
				this._positionsOptionsHelperService.isLoadingPositionsOptions$.next(false)
				throw error
			}),
		)
	}

	public executePositionOption(symbol: string): Observable<ExecutePositionOptionResponseInterface> {
		return this._http
			.post<ExecutePositionOptionResponseInterface>(
				`${this._environmentsHelperService.apiUrl}/options/v1/option/${symbol}/exercise/`,
				{},
			)
			.pipe(
				tap((): void => {
					this._positionsOptionsHelperService.removePosition(symbol)
				}),
			)
	}

	public closePositionOption(position: ClosePositionInterface): Observable<OrderInterface> {
		return this._http.post<OrderInterface>(
			`${this._environmentsHelperService.apiUrl}/orders/close_positions/`,
			position,
		)
	}
}
